import {inject, Injectable} from "@angular/core";
import {HttpClient} from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import {of, Observable, throwError} from 'rxjs';

// Dependencias de entorno y específicas del modulo Tools_Ai
import {environment as global_env} from "@env/environment";
import {environment as llm_env} from "@env/environments.llm-dev";
import {NavigatorDeviceDetectorService} from '@llm/service/navigator_device_detector.service';
import {llmStoreService} from '@llm/service/llm_store.service';

/**
 * @Injectable
 * @description
 * Servicio centralizado para el registro de logs.
 *
 * @design_pattern
 * Este servicio implementa una doble API para equilibrar conveniencia y control:
 * 1. MÉTODOS DE CONTROL (`log`, `debug`, `warn`, `error`): Devuelven `Observable<void>`.
 * Permiten al código consumidor reaccionar al resultado de la operación de logging (éxito/error).
 * Ideales para casos críticos donde se necesita una acción post-logging.
 *
 * 2. MÉTODO DE CONVENIENCIA (`info`): Devuelve `void`.
 * Abstrae la suscripción y proporciona una interfaz "fire-and-forget".
 * Ideal para el 95% de los casos de logging donde no se requiere una acción de seguimiento.
 */
@Injectable({providedIn: 'root'})
export class Tools_aiConsoleService {
  // --- DEPENDENCIAS ---
  private _detector = inject(NavigatorDeviceDetectorService);
  private http = inject(HttpClient);
  private _store = inject(llmStoreService);

  // --- CONFIGURACIÓN ---
  private readonly prefix = '[LLM =>]';
  private readonly cloudFunctionUrl = 'https://us-central1-mekanicgo.cloudfunctions.net/logToCloud';

  /**
   * @private
   * @method getCallerInfo
   * @description Captura información del stack trace para identificar el origen de la llamada.
   * Es una técnica de introspección útil para la depuración, especialmente en logs remotos.
   * @returns Un objeto con la línea de la llamada y el stack completo.
   */
  private getCallerInfo(): { 1: string, stack: string[] } | { 1: string, stack: string } {
    try {
      throw new Error();
    } catch (error) {
      const stackLines = (error as Error).stack?.split('\n');
      if (stackLines && stackLines.length >= 4) {
        return {1: stackLines[3].trim(), stack: stackLines};
      }
    }
    return {1: 'N/A', stack: 'N/A'};
  }

  /**
   * @private
   * @method _cloud_console
   * @description Orquesta la llamada HTTP a la Cloud Function de logging.
   * No se suscribe; devuelve el stream para que el consumidor decida cómo y cuándo manejarlo.
   * @returns `Observable<void>` que emite al completarse o propaga un error si la llamada HTTP falla.
   */
  private _cloud_console(message: string, severity: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG', context?: any, resource?: string): Observable<void> {
    const payload = {
      message,
      severity,
      resource: resource || 'global',
      context: {
        ...context,
        userAgent: this._detector.getBrowserName(),
        url: window.location.href
      }
    };

    return this.http.post(this.cloudFunctionUrl, {data: payload}).pipe(
      // Transforma una respuesta exitosa (con cualquier cuerpo) a una emisión `void`.
      map(() => void 0),
      // Propaga el error para que el suscriptor pueda manejarlo.
      catchError(error => {
       console.error('Falha interna ao chamar a Cloud Function de logging.', error);
        return throwError(() => new Error('Falha no envio do log para a nuvem.'));
      })
    );
  }

  // --- MÉTODOS PÚBLICOS DE CONTROL (DEVUELVEN OBSERVABLE) ---

  /**
   * @method debug
   * @description Registra un log de nivel DEBUG. Devuelve un `Observable` para control granular.
   */
  debug(message: string, event?: any, resource?: string): Observable<void> {
    if (global_env.production) {
      return of(void 0);
    }

    const callerInfo = this.getCallerInfo();
    if (this._detector.isMobile() || this._detector.isTablet()) {
        return of(void 0);
      //return this._cloud_console(`${this.prefix} ${message}`, 'DEBUG', callerInfo);
    } else if (this._store.settingsToolsAi().consoleEnabled && this._store.settingsToolsAi().debugEnabled) {
      console.debug(message, event);
      return of(void 0);
    }
  }

  /**
   * @method log
   * @description Registra un log de nivel INFO. Devuelve un `Observable` para control granular.
   */
  log(message: string, event?: any, resource?: string): Observable<void> {
    if (global_env.production) {
      return of(void 0);
    }

    const callerInfo = this.getCallerInfo();
    if (this._detector.isMobile() || this._detector.isTablet()) {
        return of(void 0);
      //return this._cloud_console(`${this.prefix} ${message}`, 'INFO', callerInfo);
    } else if (this._store.settingsToolsAi().consoleEnabled && this._store.settingsToolsAi().logEnabled) {
      console.info(message, event);
      return of(void 0);
    }
  }

  /**
   * @method warn
   * @description Registra un log de nivel WARNING. Devuelve un `Observable` para control granular.
   */
  warn(message: string, event?: any, resource?: string): Observable<void> {
    if (global_env.production) {
      return of(void 0);
    }
    const callerInfo = this.getCallerInfo();
    if (this._detector.isMobile() || this._detector.isTablet()) {
        return of(void 0);
      //return this._cloud_console(`${this.prefix} ${message}`, 'WARNING', callerInfo);
    } else if (this._store.settingsToolsAi().consoleEnabled && this._store.settingsToolsAi().warningEnabled) {
      console.warn(message, event);
      return of(void 0);
    }
  }

  /**
   * @method error
   * @description Registra un log de nivel ERROR. Devuelve un `Observable` para control granular.
   */
  error(message: string, error?: any, resource?: string): Observable<void> {
    if (global_env.production) {
      return of(void 0);
    }
    const callerInfo = this.getCallerInfo();
    if (this._detector.isMobile() || this._detector.isTablet()) {
        return of(void 0);
      //return this._cloud_console(`${this.prefix} ${message}`, 'ERROR', callerInfo);
    } else if (this._store.settingsToolsAi().consoleEnabled && this._store.settingsToolsAi().errorEnabled) {
      console.error(message, error);
      return of(void 0);
    }
  }

  // --- MÉTODO PÚBLICO DE CONVENIENCIA (DEVUELVE VOID) ---

  /**
   * @deprecated
   * @method info
   * @description Punto de entrada principal para logging "fire-and-forget".
   * Selecciona el método de logging apropiado y gestiona la suscripción internamente.
   * @param message
   * @param level - El nivel de severidad del log.
   * @param resource
   */
  info(message: string, level: 'debug' | 'info' | 'warn' | 'error', resource?: string): void {
    if (!llm_env.visualize_logs) return;

    let logOperation: Observable<void> | undefined;

    switch (level) {
      case 'error':
        logOperation = this.error(message, undefined, resource);
        break;
      case 'warn':
        logOperation = this.warn(message, undefined, resource);
        break;
      case 'info':
        logOperation = this.log(message, undefined, resource);
        break;
      case 'debug':
        logOperation = this.debug(message, undefined, resource);
        break;
    }

    logOperation?.subscribe({
      error: () => {
      }
    });
  }
}
